import type { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

import { safeCall } from '@/app/utils/safeCall';

export interface UsePrevNextSliderProps {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  currentIndex: number;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
}

/**
 *
 * custom hook for prev/next slider with embla-carousel
 *
 * Read more: [Storybook docs: `usePrevNextSlider`](https://tis-web-story-book.netlify.app/?path=/story/components-sliders--with-prev-next-buttons)
 *
 * @param emblaApi EmblaCarouselType | null
 * @returns UsePrevNextSliderProps
 */
export const usePrevNextSlider = (
  emblaApi?: EmblaCarouselType | null,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void,
  onReInit?: (emblaApi: EmblaCarouselType) => void
): UsePrevNextSliderProps => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState<boolean>(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState<boolean>(true);

  const handleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const resetOrStop =
      autoplay.options.stopOnInteraction === false
        ? autoplay.reset
        : autoplay.stop;

    resetOrStop();
  }, [emblaApi]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    handleAutoplay();
    safeCall(onButtonClick, emblaApi);
  }, [emblaApi, handleAutoplay, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    handleAutoplay();
    safeCall(onButtonClick, emblaApi);
  }, [emblaApi, handleAutoplay, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentIndex(emblaApi.selectedScrollSnap());
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  const handleReInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (onReInit) {
        return safeCall(onReInit, emblaApi);
      }

      onSelect(emblaApi);
    },
    [onReInit, onSelect]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', handleReInit).on('select', onSelect);

    return () => {
      if (!emblaApi) return;
      emblaApi.off('reInit', handleReInit).off('select', onSelect);
    };
  }, [emblaApi, onSelect, handleReInit]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    currentIndex,
  };
};
